import { IFamilyMember } from '@app-types/websocket/family.types';
import { CountryDetection } from '@helpers/countryDetection';
import { FamilyMemberType } from './family.constants';

/**
 * COPPA - the US Children's Online Privacy Protection Act - protects children under 13.
 * A child that young may not be invited to an account of their own on this service.
 */
export const COPPA_MINIMUM_AGE = 13;

/**
 * Everywhere COPPA reaches: the United States together with the territories that federal
 * law covers - Puerto Rico, the US Virgin Islands, Guam, American Samoa, the Northern
 * Mariana Islands and the US Minor Outlying Islands.
 *
 * The freely associated states (Micronesia, the Marshall Islands, Palau) are deliberately
 * absent: they are sovereign countries, not US jurisdictions.
 */
const US_JURISDICTION_COUNTRIES = ['US', 'PR', 'VI', 'GU', 'AS', 'MP', 'UM'];

/**
 * The IANA time zone the device is set to, e.g. "America/New_York". Empty when there is
 * nothing to read - some older webviews have no usable Intl.
 */
const getDeviceTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (error) {
    return '';
  }
};

/**
 * True when the device's time zone places the user somewhere US law applies.
 *
 * A time zone we cannot place is treated as outside the US: it is the only reading that
 * does not block the rest of the world along with it.
 */
export const isUsTimeZone = (timeZone: string = getDeviceTimeZone()): boolean => {
  const country = CountryDetection.timeZoneToCountryMap[timeZone];
  return country !== undefined && US_JURISDICTION_COUNTRIES.indexOf(country) !== -1;
};

/**
 * True when a birth year is recorded and usable.
 */
export const hasBirthYear = (yearBorn?: number): yearBorn is number => {
  return yearBorn !== undefined && Number.isFinite(yearBorn);
};

/**
 * True unless the child is certainly already 13 - the invite has to be denied in every
 * case we cannot prove, not merely in the cases we can disprove.
 *
 * Two of those cases are not obvious. Only the year of birth is recorded, so a child born
 * exactly 13 years ago is 12 or 13 depending on whether their birthday has passed: that
 * whole year counts as under 13. And a child with no year of birth on record proves
 * nothing at all, so they are denied too.
 */
export const mayBeUnderCoppaAge = (yearBorn?: number, now: Date = new Date()): boolean => {
  if (!hasBirthYear(yearBorn)) {
    return true;
  }
  return (now.getFullYear() - yearBorn) <= COPPA_MINIMUM_AGE;
};

/**
 * True when COPPA stops us from opening the invite dialog for this family member, and the
 * parent should be told why instead.
 */
export const isCoppaBlockedInvite = (
  member: IFamilyMember | undefined,
  timeZone: string = getDeviceTimeZone(),
  now: Date = new Date(),
): boolean => {
  if (member === undefined || member.role !== FamilyMemberType.CHILD) {
    return false;
  }
  return isUsTimeZone(timeZone) && mayBeUnderCoppaAge(member.yearBorn, now);
};



// WEBPACK FOOTER //
// ./src/services/family/coppa.ts